import {
  Button,
  DateRangePickerField,
  Flex,
  Form,
  SelectField,
} from '@/components';
import { useForm } from '@/hooks';
import { zodResolver } from '@hookform/resolvers/zod';
import dayjs from 'dayjs';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';
import { useBatchSearchQueryStates } from '../_hooks/search-query-states';
import {
  BatchSearchFormSchema,
  BatchTypeLabelModifier,
  batchType,
} from '../_models/batch';

type FormValues = z.infer<typeof BatchSearchFormSchema>;

export const BatchSearchForm: FC = () => {
  const { t } = useTranslation();
  const [searchQueryStates, setSearchQueryStates] = useBatchSearchQueryStates();

  const {
    control,
    formState: { errors },
    handleSubmit,
  } = useForm<FormValues>({
    defaultValues: {
      batchType: searchQueryStates.batchType,
    },
    resolver: zodResolver(BatchSearchFormSchema),
  });

  const onSubmit = ({ batchType, createdDateRange }: FormValues) => {
    const createdAtFrom = createdDateRange?.[0]
      ? dayjs(createdDateRange[0]).format('YYYY-MM-DD')
      : null;
    const createdAtTo = createdDateRange?.[1]
      ? dayjs(createdDateRange[1]).format('YYYY-MM-DD')
      : null;

    setSearchQueryStates({
      batchType: batchType ?? 'all',
      createdAtFrom,
      createdAtTo,
      page: 1,
    });
  };

  return (
    <Form
      leavePageAlertCondition={null}
      layout="vertical"
      style={{ marginBottom: 12 }}
      onFinish={handleSubmit(onSubmit)}
    >
      <Flex gap="0 16px" wrap="wrap">
        <SelectField
          name="batchType"
          label={t('batch.type')}
          control={control}
          errors={errors}
          options={[
            { value: 'all', label: t('batch.type-label-all') },
            ...batchType.options.map((value) => ({
              value,
              label: BatchTypeLabelModifier[value],
            })),
          ]}
          wrapperStyle={{ marginBottom: 0 }}
          width={160}
        />
        <DateRangePickerField
          name="createdDateRange"
          label={t('batch.start-date')}
          control={control}
          errors={errors}
          allowEmpty={[true, true]}
          wrapperStyle={{ marginBottom: 0 }}
        />
        <Button type="primary" htmlType="submit" style={{ marginTop: 30 }}>
          {t('search')}
        </Button>
      </Flex>
    </Form>
  );
};
